import { useMemo } from 'react'
import {
  PAYMENT_METHOD_CONFIGS,
  PaymentMethodConfig,
  paymentMethodMap,
} from 'modules/flow/components/payment-methods-step/utils/payment-method-configs'
import { PaymentMethod, PaymentProcessor } from 'models/ChargeIntent'
import useConfig from 'modules/config/state/use-config'
import useOrder from 'modules/order/state/order/use-order'
import { shouldShowDW } from 'modules/order/state/payment-details/payment-details-utils'

// Main hook to fetch active payment method configurations
export const useActivePaymentMethodConfigs = (): PaymentMethodConfig[] => {
  const config = useConfig()
  const order = useOrder()

  // Get relevant states
  const isDWEnabled = shouldShowDW(config) && order.stripePaymentRequest.canMakePayment
  const isACHEnabled = config.planFeatures.ach_account_and_routing
  const walletType = order.stripePaymentRequest.walletType

  // Memoize the calculation of active payment methods
  return useMemo(() => {
    const seenTypes: { [key: string]: boolean } = {}

    const unsortedConfigs = config.payment.activeMethods.reduce<PaymentMethodConfig[]>(
      (result, paymentMethod) => {
        let paymentType = paymentMethod.type

        if (seenTypes[paymentType]) {
          return result
        }

        /**
         * For MVP, we only support Paypal Commerce
         * See. https://classydev.atlassian.net/browse/CL-41943?focusedCommentId=275397
         */
        if (paymentType === PaymentMethod.PayPal && paymentMethod.name !== 'PAYPAL_COMMERCE') {
          return result
        }

        // use Plaid for ACH unless org settings enable non-Plaid ACH
        if (paymentType === PaymentMethod.ACH && !isACHEnabled) {
          paymentType = PaymentMethod.Plaid
        }

        // Conditionally filter out DW
        if (paymentType === PaymentMethod.DW && !isDWEnabled) {
          return result
        }

        seenTypes[paymentType] = true

        const config = getPaymentMethodConfig(paymentType, walletType)

        result.push({
          ...config,
          key: paymentType,
          processor: paymentMethod.name as PaymentProcessor,
        })

        return result
      },
      [],
    )

    // Sort the configs by the order attribute
    return unsortedConfigs.sort((a, b) => a.order - b.order)
  }, [isDWEnabled, isACHEnabled, walletType, config.payment])
}

// Utility function to get the config for a given payment method
export const getPaymentMethodConfig = (
  paymentMethod: PaymentMethod,
  walletType: string | null,
): PaymentMethodConfig => {
  const effectiveWalletType = paymentMethod === PaymentMethod.DW ? walletType : null

  return effectiveWalletType
    ? PAYMENT_METHOD_CONFIGS[effectiveWalletType]
    : PAYMENT_METHOD_CONFIGS[paymentMethodMap[paymentMethod]]
}
